/**
 * Where a record opened from ANOTHER record's tab returns to (BP-97, `?from=`).
 *
 * Luis 2026-10-12 (aircraft 178): "clicking an inspection name opens the inspection edit in a
 * new tab, but clicking Close does nothing … Need to check all the link clicking in aircraft".
 * A linked record now opens in the SAME browser tab and its Close / Save & Close come back to
 * the aircraft tab it was opened from.
 *
 * The query is the Components record's shape (`component/items/componentRecord.ts`):
 * `?from=aircraft&aircraft=<id>`, plus `from_tab=<the aircraft tab's key>` — not `tab`, which
 * the opened record may use for its OWN tab (an inspection's "?tab=task_list"). The aircraft
 * record opens `?tab=<key>` through useTabFromQuery (keys match case-insensitively).
 *
 * VALIDATED, never used as given: only an aircraft of a positive integer id; anything else is
 * no return, and the record keeps its own Close.
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** The aircraft record's tab keys a linked record can be opened from (AircraftDetail `key ?? label`). */
export const AIRCRAFT_RETURN_TABS = [
  "Overview",
  "Log Book",
  "Inspections",
  "Components",
  "ADs",
  "SBs",
  "Modifications",
  "History",
  "Maintenance Log",
  "Flight Log",
  "Jobs",
] as const;
export type AircraftReturnTab = (typeof AIRCRAFT_RETURN_TABS)[number];

export interface AircraftReturn {
  aircraftId: number;
  tab: AircraftReturnTab;
}


/** `href` with the return appended (its own query kept). A null href stays null. */
export function withAircraftReturn(href: string, ret: AircraftReturn): string;
export function withAircraftReturn(href: string | null, ret: AircraftReturn): string | null;
export function withAircraftReturn(href: string | null, ret: AircraftReturn): string | null {
  if (!href) return href;
  const [path, query = ""] = href.split("?");
  const params = new URLSearchParams(query);
  params.set("from", "aircraft");
  params.set("aircraft", String(ret.aircraftId));
  params.set("from_tab", ret.tab);
  return `${path}?${params.toString()}`;
}

/** The aircraft tab to return to, read off the opened record's query; null when there is none. */
export function aircraftReturnUrl(params: URLSearchParams): string | null {
  if (params.get("from") !== "aircraft") return null;
  const aircraftId = Number(params.get("aircraft"));
  if (!Number.isInteger(aircraftId) || aircraftId <= 0) return null;
  const tab = params.get("from_tab") ?? "";
  return (AIRCRAFT_RETURN_TABS as readonly string[]).includes(tab)
    ? `/aircraft/${aircraftId}?tab=${encodeURIComponent(tab)}`
    : `/aircraft/${aircraftId}`;
}

/** For a record's Close / Save & Close: the aircraft tab it was opened from, else null. */
export function useAircraftReturnUrl(): string | null {
  const [params] = useSearchParams();
  return useMemo(() => aircraftReturnUrl(params), [params]);
}
